import { useId, useState } from 'react'

import type { CsvImportResult } from '@/api/market'

export interface CsvImportFieldProps {
  label: string
  header: string
  onImport: (csv: string) => Promise<CsvImportResult>
}

/**
 * A file picker that reads a `.csv` file as text in the browser and hands it
 * to the mock client to parse — no upload, no server. Shows what was added
 * and any rows that could not be read.
 */
export function CsvImportField({ label, header, onImport }: CsvImportFieldProps) {
  const inputId = useId()
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<CsvImportResult | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  /** `FileReader`, not `Blob.text()` — more consistently supported across browsers and test environments. */
  function readText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
      reader.onerror = () => reject(reader.error ?? new Error('could not read file'))
      reader.readAsText(file)
    })
  }

  async function handleFile(file: File) {
    setBusy(true)
    setResult(null)
    setFailure(null)
    try {
      const text = await readText(file)
      setResult(await onImport(text))
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not import this file.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mk-csv">
      <label className="mk-csv__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        type="file"
        accept=".csv,text/csv"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      <p className="mk-csv__hint">One row per line, comma-separated. Header: {header}</p>
      {failure ? (
        <p className="mk-csv__failure" role="alert">
          {failure}
        </p>
      ) : null}
      {result ? (
        <p className="mk-csv__result" role="status">
          {result.added} row{result.added === 1 ? '' : 's'} added.
          {result.errors.length > 0 ? ` ${result.errors.length} row${result.errors.length === 1 ? '' : 's'} skipped:` : ''}
          {result.errors.length > 0 ? (
            <ul className="mk-csv__errors">
              {result.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          ) : null}
        </p>
      ) : null}
    </div>
  )
}
